import { useCallback, useRef, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { moveCut } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import type { DragGuide } from './TimelineGuide';
import { beatSec, pointerToSec, snapTime, type TimelineView } from './timelineGeometry';

interface UseEdgeDragProps {
  laneRef: RefObject<HTMLDivElement | null>;
  view: TimelineView;
  snapOn: boolean;
  onGuide: (guide: DragGuide | null) => void;
}

/**
 * Drags the cut between two clips (design c5: "Dragging a clip edge also snaps; the next clip gets
 * shorter or longer, so the video never has a gap"). Alt holds the snap off. One drag is one Undo
 * step. The arrow keys move a focused edge by one beat.
 */
export function useEdgeDrag({ laneRef, view, snapOn, onGuide }: UseEdgeDragProps) {
  const { change, songMap, doc } = useMusicVideoEditor();
  // The trim stops where a clip runs out of footage (`maxClipSpanSec`).
  const limitsFor = useClipLimits();
  const dragging = useRef<number | null>(null);

  const placeAt = useCallback(
    (index: number, clientX: number, altKey: boolean) => {
      const lane = laneRef.current;
      if (lane === null) {
        return;
      }
      const free = !snapOn || altKey;
      const sec = snapTime(pointerToSec(clientX, lane.getBoundingClientRect(), view), songMap.beats, songMap.bpm, free);
      onGuide({ sec, free });
      change((current) => moveCut(current, index, sec, limitsFor()), `edge:${index}`);
    },
    [change, laneRef, limitsFor, onGuide, snapOn, songMap.beats, songMap.bpm, view]
  );

  const onPointerDown = useCallback((index: number, event: PointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragging.current = index;
  }, []);

  const onPointerMove = useCallback(
    (index: number, event: PointerEvent<HTMLElement>) => {
      if (dragging.current === index) {
        placeAt(index, event.clientX, event.altKey);
      }
    },
    [placeAt]
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      dragging.current = null;
      onGuide(null);
    },
    [onGuide]
  );

  const onKeyDown = useCallback(
    (index: number, event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }
      event.preventDefault();
      const clip = doc.clips[index];
      if (clip === undefined) {
        return;
      }
      const step = (event.key === 'ArrowLeft' ? -1 : 1) * beatSec(songMap.bpm);
      const sec = snapTime(clip.endSec + step, songMap.beats, songMap.bpm, !snapOn || event.altKey);
      change((current) => moveCut(current, index, sec, limitsFor()), `edge:${index}`);
    },
    [change, doc.clips, limitsFor, snapOn, songMap.beats, songMap.bpm]
  );

  return { onPointerDown, onPointerMove, onPointerUp, onKeyDown };
}
